import { useContext, useEffect, useState } from 'react';
import { Box, Button, Chip, DialogContentText, IconButton, LinearProgress, Paper, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
    Task, SelfImprovement, Bolt, Gavel, Block, DryCleaning,
    Checkroom,
    CalendarToday,
    Delete,
    Edit,
    HowToVote,
    PhotoCamera,
    PlayArrow,
    Wash,
    Check,
    Visibility,
    Timer,
    AutoAwesome,
    Print,
} from '@mui/icons-material';
import { cloneObject, convertToCountTypeString, isOutfitItem } from '../utilities/utils';
import {
    BaseBasketItem, ISettingsItem, BaseTimedBasketItem,
    IEndEventTriggered, TaskBasketItem, PenaltyBasketItem, ITaskRating, PositionBasketItem,
} from '../ts/Definitions';
import { CountType, ItemTypes } from '../ts/enums';
import ImageDisplayField from '../fields/ImageDisplayField';
import { green, grey, orange } from '@mui/material/colors';
import { SelectedSettingsContext } from '../ts/Contexts';
import { examineTask, isMetronomePositionItem, isSiteEffectsPositionItem } from '../utilities/taskUtils';
import Emitter, { ShowMetronomeDialog, ShowSettingItemEditorDialog, ShowSiteEffectsOverlay } from '../utilities/Emitter';
import TaskRatingWidget from '../components/TaskRatingWidget';
import { printScheduleEvent } from '../utilities/schedulePrintUtils';

export interface ScheduleItemProps {

    item: BaseBasketItem,
    play: ((item: ISettingsItem) => void),
    remove?: ((item: ISettingsItem) => void),
    postpone?: ((item: ISettingsItem) => void),
    releaseEvent?: ((item: ISettingsItem) => void),
    requestProof?: ((item: TaskBasketItem | PenaltyBasketItem) => void),
    changePermission?: ((item: ISettingsItem) => void),
    changeTask?: ((item: ISettingsItem) => void),
    failed?: ((item: ISettingsItem) => void),
    showRemainingTime: boolean,
    defaultImage: string,
    checkEventComplete: (item: IEndEventTriggered) => boolean,
    isOwnerView?: boolean,
    readonly?: boolean,
    viewVote?: (item: ISettingsItem) => void;
    isOwned: boolean;
    hideSubTasks?: boolean;
    hideImages?: boolean;
    hideDescriptions?: boolean;
    previewBasket?: boolean;
    onSiteEffectsComplete?: () => void;
    compactMode?: boolean;
    hidePrintButton?: boolean;
}

interface Props extends ScheduleItemProps {
    showScheduleDialog: (props: ScheduleItemProps) => void;
}

const stripOutDuplicates = (text: string): string => {
    const words = text.split(' ');
    const seenWords = new Set<string>();
    const result: string[] = [];

    // Iterate over the words from the end to the beginning
    for (let i = words.length - 1; i >= 0; i--) {
        const word = words[i];
        if (!seenWords.has(word)) {
            seenWords.add(word);
            result.unshift(word); // Add the word to the beginning of the result array
        }
    }

    return result.join(' ');
}

export default (props: Props): JSX.Element => {
    const { item, defaultImage, previewBasket,
        checkEventComplete,
        showScheduleDialog } = props;
    const settings = useContext(SelectedSettingsContext);
    const settingProps = examineTask(settings, props);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isMetronome = isMetronomePositionItem(item);
    const isSiteEffects = isSiteEffectsPositionItem(item);
    const isCompactMode = !!props.compactMode;

    // ── Outfit difficulty rating ──────────────────────────────────
    const [outfitRating, setOutfitRating] = useState<ITaskRating>({});
    const isOutfit = item instanceof TaskBasketItem && isOutfitItem(item as any);
    useEffect(() => {
        if (!isOutfit) return;
        const choiceName = (item as TaskBasketItem).selectedChoiceName ?? 'default';
        const safeKey = choiceName.replace(/[.#$[\]/]/g, '_');
        settings.loadOutfitRatingsForTask(item.key).then((ratings) => {
            setOutfitRating(ratings?.[safeKey] ?? {});
        }).catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.key, (item as TaskBasketItem).selectedChoiceName]);

    // ── Type icon ────────────────────────────────────────────────
    const typeIcon = settingProps.showTaskIcon ? <Task sx={{ color: orange[500], fontSize: 22 }} />
        : settingProps.showOutfitIcon ? <DryCleaning sx={{ color: orange[500], fontSize: 22 }} />
            : settingProps.showClothesIcon ? <Checkroom sx={{ color: orange[500], fontSize: 22 }} />
                : settingProps.showPenaltyIcon ? <Gavel sx={{ color: orange[500], fontSize: 22 }} />
                    : settingProps.showPermissionIcon ? <Block sx={{ color: orange[500], fontSize: 22 }} />
                        : settingProps.showEventIcon ? <Bolt sx={{ color: orange[500], fontSize: 22 }} />
                            : settingProps.showMetronomeIcon ? <Timer sx={{ color: orange[500], fontSize: 22 }} />
                                : settingProps.showSiteEffectsIcon ? <AutoAwesome sx={{ color: orange[500], fontSize: 22 }} />
                                    : settingProps.showPositionIcon ? <SelfImprovement sx={{ color: orange[500], fontSize: 22 }} />
                                        : null;

    // ── Status chips ─────────────────────────────────────────────
    const statusChips: JSX.Element[] = [];
    if (settingProps.showRemainingMinutes && settingProps.inProgress
        && ((!settingProps.isAwaitingTimeTrigger && !settingProps.triggerEventDef) || (settingProps.triggerEventDef && settingProps.isAwaitingTimeTrigger))) {
        statusChips.push(
            <Chip key="remaining" size="small" label={(item as BaseTimedBasketItem).remainingMinutes} sx={{ fontSize: 11 }} />
        );
    }
    if (settingProps.inProgress && (item as TaskBasketItem).endTriggerEventName) {
        statusChips.push(
            <Chip key="until" size="small" label={`Until ${(item as TaskBasketItem).endTriggerEventName}`} sx={{ fontSize: 11 }} />
        );
    }
    if (settingProps.showRemainingPercent && settingProps.inProgress
        && ((!settingProps.awaitingEventTask && !(item as TaskBasketItem).endTriggerEventName) || (settingProps.awaitingEventTask && settingProps.triggerEventDef))) {
        statusChips.push(
            <Chip key="percent" size="small" label={`${Math.round(100 - settingProps.percentRemaining)}%`} sx={{ fontSize: 11 }} />
        );
    }
    if (settingProps.showPermanentMessage) {
        statusChips.push(
            <Chip key="perm" size="small" label="Permanent" variant="outlined" sx={{ fontSize: 11 }} />
        );
    }
    if ((item as TaskBasketItem | PenaltyBasketItem).autoStartedByRequestedBy) {
        statusChips.push(
            <Chip key="autostarted" size="small" color="info" variant="outlined" label="Auto-started by parent" sx={{ fontSize: 11 }} />
        );
    }
    if (settingProps.showPendingItemTimeInfo) {
        statusChips.push(
            <Chip key="pending" size="small" color="warning" label={`Pending${settingProps.showTimeDueInfo ? settingProps.pendingDueTimeInfo : ''}`} sx={{ fontSize: 11 }} />
        );
        if (settingProps.showPenaltyForMissingMessage) {
            statusChips.push(
                <Chip key="penalty" size="small" color="error" variant="outlined" label={settingProps.penaltyForMissingInfo} sx={{ fontSize: 11 }} />
            );
        }
    }

    const openMetronomeDialog = (onStart?: (timing: { durationMs: number, intervalMs: number }) => void) => {
        const metronomeItem = item as PositionBasketItem;
        Emitter.emit(ShowMetronomeDialog, {
            title: item.name,
            taskKey: metronomeItem.taskKey,
            audioUrl: metronomeItem.audioUrl,
            onStart,
        });
    };

    const openSiteEffectsOverlay = (durationSec?: number) => {
        const siteEffectsItem = item as PositionBasketItem;
        Emitter.emit(ShowSiteEffectsOverlay, {
            title: item.name,
            config: siteEffectsItem.siteEffectsSettings,
            durationSec,
            ownerTest: false,
            onComplete: props.onSiteEffectsComplete,
        });
    };

    const handleOpenItem = (ev?: React.MouseEvent) => {
        ev?.preventDefault();
        ev?.stopPropagation();
        if (isMetronome) {
            openMetronomeDialog(
                !props.readonly && !props.isOwnerView && item.isPaused && !item.isComplete
                    ? (timing) => {
                        const metronomeInfo = {
                            ...settings.getMetronomeInfo(),
                            duration: Math.round(timing.durationMs / 1000),
                            intervalMs: timing.intervalMs,
                        };
                        settings.updateMetronomeState(metronomeInfo as any);
                        props.play(item);
                    }
                    : undefined
            );
            return;
        }
        if (isSiteEffects) {
            if (!props.readonly && !props.isOwnerView && item.isPaused && !item.isComplete) {
                props.play(item);
                openSiteEffectsOverlay((item as BaseTimedBasketItem).timerTicks || (item as PositionBasketItem).duration);
            } else if (!props.readonly && !props.isOwnerView && !item.isPaused && !item.isComplete) {
                openSiteEffectsOverlay((item as BaseTimedBasketItem).timerTicks || (item as PositionBasketItem).duration);
            }
            return;
        }
        if (showScheduleDialog) showScheduleDialog(props);
    };

    // ── Action buttons ───────────────────────────────────────────
    const actions: JSX.Element[] = [];
    const showCompactQuickComplete = isCompactMode
        && !settingProps.isPending
        && (settingProps.showDoneButton || settingProps.showDecrementCountButton);
    if (!item.isComplete && settingProps.allowPlayScheduleItem) {
        if (!previewBasket
            && !settingProps.isPending
            && !props.isOwnerView
            && !settingProps.showOkButton
            && !showCompactQuickComplete
            && !settingProps.showAwaitingRequestedByMessage) {
            const icon = settingProps.startText === 'Begin' ? <PlayArrow fontSize="small" /> : <Visibility fontSize="small" />;
            actions.push(<Button key="play" size="small" variant="contained" color="primary" startIcon={icon} onClick={handleOpenItem}>{settingProps.startText}</Button>
            );
        }
        if (!previewBasket && !settingProps.isPending && settingProps.showOkButton && !props.isOwnerView) {
            actions.push(<Button key="ok" size="small" variant="contained" color="success" startIcon={<Check />} onClick={(ev) => { settingProps.ok(); ev.preventDefault(); ev.stopPropagation(); }}>Acknowledge</Button>
            );
        }
        if (showCompactQuickComplete) {
            const countText = settingProps.showDecrementCountButton
                ? ` ${String((item as TaskBasketItem).expectedCount ?? "")} ${CountType[(item as TaskBasketItem).countType] ?? ""}`
                : "";
            actions.push(<Button key="quick-complete" size="small" variant="contained" color="success" startIcon={<Check />} onClick={(ev) => { settingProps.done(); ev.preventDefault(); ev.stopPropagation(); }}>Completed{countText}</Button>
            );
        }
        if (settingProps.showRemoveButton) {
            actions.push(<Button key="remove" size="small" variant="outlined" color="error" startIcon={<Delete />} onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); settingProps.remove(); }}>Remove</Button>
            );
        }
        if (settingProps.showPostponeButton) {
            actions.push(<Button key="postpone" size="small" variant="outlined" startIcon={<CalendarToday />} onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); settingProps.postpone(); }}>Postpone</Button>
            );
        }
        if (settingProps.showProofButton && !settingProps.isPending) {
            actions.push(<Button key="proof" size="small" variant="outlined" startIcon={<PhotoCamera />} onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); settingProps.proof(); }}>Proof</Button>
            );
        }
        if (settingProps.showReleaseButton) {
            actions.push(<Button key="release" size="small" variant="outlined" startIcon={<PlayArrow />} onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); settingProps.release(); }}>Release</Button>
            );
        }
        if (props.isOwnerView) {
            actions.push(<Button key="settings" size="small" variant="outlined" startIcon={<Edit />} onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); Emitter.emit(ShowSettingItemEditorDialog, cloneObject(settings.getCurrentItem(item)), settings); }}>Settings</Button>
            );
        }
        if (!previewBasket && settingProps.hasHygiene && settings.isHygieneAllowed()) {
            actions.push(<Button key="hygiene" size="small" variant="outlined" startIcon={<Wash />} onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); settingProps.hygiene(); }}>Hygiene</Button>
            );
        }
        if (settingProps.showChangeButton) {
            actions.push(<Button key="change" size="small" variant="outlined" startIcon={<Edit />} onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); settingProps.change(); }}>Change</Button>
            );
        }
        if (settingProps.showViewVoteButton && !settingProps.isPending) {
            actions.push(<Button key="vote" size="small" variant="outlined" startIcon={<HowToVote />} onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); settingProps.viewVote(); }}>View Vote</Button>
            );
        }
    }

    if (!previewBasket && item.itemType === ItemTypes.event && !props.hidePrintButton) {
        actions.push(<Button key="print" size="small" variant="outlined" startIcon={<Print />} onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            printScheduleEvent(item, item.getBasketText(checkEventComplete));
        }}>Print</Button>
        );
    }

    const progressPct = settingProps.showPercentComplete ? (100 - settingProps.percentRemaining) : 0;

    const compactStatusChips = isCompactMode ? statusChips.slice(0, 2) : statusChips;

    return (
        <Paper
            variant="outlined"
            sx={{
                mt: isCompactMode ? 0.75 : 1.5,
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                cursor: 'pointer',
                transition: 'box-shadow 0.15s, border-color 0.15s',
                '&:hover': {
                    borderColor: orange[300],
                    boxShadow: `0 0 0 2px ${orange[100]}`,
                },
            }}
            onClick={handleOpenItem}
        >
            {/* Progress bar overlay */}
            {settingProps.showPercentComplete && (
                <LinearProgress
                    variant="determinate"
                    value={progressPct}
                    sx={{
                        height: 3,
                        borderRadius: 0,
                        bgcolor: grey[100],
                        '& .MuiLinearProgress-bar': { bgcolor: green[400] },
                    }}
                />
            )}

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: isCompactMode ? 0.75 : 1, p: isCompactMode ? { xs: 0.75, sm: 1 } : { xs: 1, sm: 1.5 } }}>
                {/* Type icon + optional image */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, pt: 0.25, flexShrink: 0 }}>
                    {typeIcon}
                    {!isCompactMode && settingProps.showTaskImage && (
                        <Box onClick={(ev) => ev.stopPropagation()}>
                            <ImageDisplayField
                                item={settingProps.settingsItem ?? item}
                                defaultImage={defaultImage}
                                noImageValidation={false}
                                allowFix={settings.isLoggedOnUserSession() || settings.isSettingsOwner()}
                                allowUpdate={settings.isSettingsOwner()}
                                updated={settingProps.imageUpdatedCallback}
                            />
                        </Box>
                    )}
                </Box>

                {/* Text content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant={isCompactMode ? 'body2' : 'caption'} sx={{ fontWeight: 500, color: 'text.primary', overflowWrap: 'anywhere' }}>
                        {item.getBasketText && item.getBasketText(checkEventComplete)}
                        {settingProps.showTimerSetValue && (
                            <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
                                (Timer: {convertToCountTypeString((item as BaseTimedBasketItem).expectedCount ?? 0, CountType.minutes)})
                            </Typography>
                        )}
                    </Typography>

                    {/* Outfit rating — interactive popover widget */}
                    {isOutfit && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25 }}>
                            <TaskRatingWidget
                                rating={outfitRating}
                                label="Rate:"
                                onRate={(r) => {
                                    const choiceName = (item as TaskBasketItem).selectedChoiceName ?? 'default';
                                    settings.saveOutfitRating(item.key, choiceName, r);
                                    setOutfitRating(r);
                                }}
                            />
                        </Box>
                    )}

                    {/* Description — desktop only */}
                    {!isCompactMode && settingProps.canShowDescription && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' }, mt: 0.25, overflowWrap: 'anywhere' }}>
                            {item.description}
                        </Typography>
                    )}

                    {/* Status chips */}
                    {compactStatusChips.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                            {compactStatusChips}
                        </Box>
                    )}

                    {settingProps.showAwaitingRequestedByMessage && (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.75 }}>
                            {settingProps.awaitingRequestedByMessage}
                        </Typography>
                    )}

                    {/* Actions */}
                    {actions.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }} onClick={(ev) => ev.stopPropagation()}>
                            {actions}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Hygiene info */}
            {!isCompactMode && settingProps.showHygieneInfo && (
                <DialogContentText sx={{ px: 1.5, pb: 1, fontSize: 12 }}>
                    {!settings.getGeneralSettings().hygieneIntervalMins ? '' : 'Timed '}Hygiene Breaks — {settings.getNextHygieneDate()}
                </DialogContentText>
            )}

            {/* Sub-tasks */}
            {!isCompactMode && settingProps.showUsingSubTasks
                && (item instanceof TaskBasketItem || item instanceof PenaltyBasketItem)
                && (
                    <Box sx={{ px: 1.5, pb: 1 }}>
                        <Typography variant="caption" color="text.secondary">Using:</Typography>
                        {settingProps.getSubTasks().map((innerTask, idx) => (
                            <Box key={innerTask.key + idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }} onClick={(ev) => ev.stopPropagation()}>
                                <ImageDisplayField
                                    item={innerTask}
                                    defaultImage={defaultImage}
                                    size='Small'
                                    noImageValidation={false}
                                    allowFix={settings.isLoggedOnUserSession() || settings.isSettingsOwner()}
                                    updated={(newVal) => {
                                        innerTask.img = newVal;
                                        if (innerTask) {
                                            innerTask.img = newVal;
                                            settings.updateSettingsItem(innerTask, false, true);
                                        }
                                    }}
                                />
                                <Typography variant="caption">
                                    {stripOutDuplicates(`${innerTask.name + (item.selectedItems && item.selectedItems[innerTask.key] && item.selectedItems[innerTask.key] !== innerTask.name ? ` ${item.selectedItems[innerTask.key]}` : '')}`)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
        </Paper>
    );
}
